import { CandidacyStatus, MinimalCandidacy } from "@/types";

// The pair a staffer's Stevie route leaves behind, on whichever candidacy shape
// the caller has (pipeline rows carry status as a change, dashboard rows, flat).
type StevieRouteMarks = {
  status: CandidacyStatus;
  stevieNudgedAt: string | null;
  stevieCallEndedAt: string | null;
};

// A staffer's Stevie route only shapes the pipeline while the candidacy is
// still in Applied. The nudge fields persist as history after it moves on.
const isApplied = (candidacy: MinimalCandidacy) =>
  candidacy.lastCandidacyStatusChange.toStatus === CandidacyStatus.APPLIED;

const isAwaitingCallByMarks = (marks: StevieRouteMarks): boolean =>
  marks.status === CandidacyStatus.APPLIED &&
  marks.stevieNudgedAt !== null &&
  marks.stevieCallEndedAt === null;

export const stevieRoute = {
  /** Sent to Stevie, still waiting on the call — muted out of the main queue. */
  isAwaitingCall: (candidacy: MinimalCandidacy): boolean =>
    isAwaitingCallByMarks({
      status: candidacy.lastCandidacyStatusChange.toStatus,
      stevieNudgedAt: candidacy.stevieNudgedAt,
      stevieCallEndedAt: candidacy.stevieCallEndedAt,
    }),

  /** Same test for shapes that carry a flat status, like dashboard rows. */
  isAwaitingCallByMarks,

  /** Shortlisted off a staffer's Stevie route rather than by hand. */
  isShortlistedViaStevie: (candidacy: MinimalCandidacy): boolean =>
    candidacy.lastCandidacyStatusChange.toStatus === CandidacyStatus.SHORTLIST &&
    candidacy.stevieNudgedAt !== null,

  /** Took the Stevie call and is back in the queue with more to review. */
  isBackFromCall: (candidacy: MinimalCandidacy): boolean =>
    isApplied(candidacy) &&
    candidacy.stevieNudgedAt !== null &&
    candidacy.stevieCallEndedAt !== null,

  /** Time in stage runs from the nudge, then from the call that answered it. */
  stageEnteredAt: (candidacy: MinimalCandidacy): string => {
    if (!isApplied(candidacy) || candidacy.stevieNudgedAt === null) {
      return candidacy.lastCandidacyStatusChange.createdAt;
    }

    return candidacy.stevieCallEndedAt ?? candidacy.stevieNudgedAt;
  },
};
